<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <span class="material-symbols-outlined">precision_manufacturing</span>
          </div>
          <h4 class="title">{{ $t("suppliers.suppliers") }}</h4>
        </md-card-header>
        <md-card-content>
          <md-table v-model="tableData" table-header-color="green" class="table-sticky">
            <md-table-row slot="md-table-row" slot-scope="{ item }">
              <md-table-cell :md-label="$t('suppliers.actions')">
                <div class="md-table-cell-container cell-actions">
                  <md-button class="md-icon-button" v-if="hasPermission('Write', 'Suppliers', 'Edit')" @click="showEditModal(item)">
                    <md-icon>edit</md-icon>
                  </md-button>
                </div>
              </md-table-cell>
              <md-table-cell v-if="hasPermission('Read', 'Suppliers', '')" md-label="ID">{{ item.id }}</md-table-cell>
              <md-table-cell :md-label="$t('suppliers.abbreviation')">{{ item.abbreviation }}</md-table-cell>
              <md-table-cell v-if="hasPermission('LimitRead', 'Suppliers', 'Name')" :md-label="$t('suppliers.name')">{{ item.name }}</md-table-cell>
              <md-table-cell :md-label="$t('suppliers.nameFull')">{{ item.nameFull }}</md-table-cell>
              <md-table-cell :md-label="$t('suppliers.contact')">{{ item.contact }}</md-table-cell>
              <md-table-cell :md-label="$t('suppliers.email')">{{ item.email }}</md-table-cell>
              <md-table-cell :md-label="$t('suppliers.leadTimeMonths')">{{
                item.leadTimeMonths != null ? $options.filters.formatUnit(item.leadTimeMonths, $t("suppliers.months"), 1, 1) : ""
              }}</md-table-cell>
              <md-table-cell :md-label="$t('suppliers.creditor')">{{ item.creditor }}</md-table-cell>
              <md-table-cell :md-label="$t('suppliers.bankId')">{{ item.bankId }}</md-table-cell>
            </md-table-row>
          </md-table>
        </md-card-content>
      </md-card>
    </div>
    <div class="sp-dock">
      <a v-if="hasPermission('Write', 'Suppliers', 'Create')" class="sp-dock-trigger" :title="$t('suppliers.createSupplier')" @click="showCreateModal">
        <md-icon>add</md-icon>
      </a>
      <div class="sp-dock-search">
        <a class="sp-dock-trigger" :class="{ 'sp-dock-trigger--active': searchQuery }" @click="toggleDockSearch">
          <md-icon>search</md-icon>
        </a>
        <div v-show="dockSearchOpen" class="sp-dock-search-panel">
          <md-field md-clearable>
            <label>{{ $t("suppliers.search") }}</label>
            <md-input ref="dockSearchInput" v-model="searchQuery"></md-input>
          </md-field>
        </div>
      </div>
    </div>
    <modal v-if="createModal" @close="hideCreateModal" class-name="shipping-plan-modal-container">
      <template slot="header">
        <h4 class="modal-title">{{ $t("suppliers.createSupplier") }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="hideCreateModal">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <ValidationObserver v-slot="{}" ref="createForm">
          <ValidationProvider name="abbreviation" rules="alpha_num|length:4" v-slot="{ passed, failed, errors }">
            <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
              <label>{{ $t("suppliers.abbreviation") }}</label>
              <md-input v-model="abbreviation" type="text" maxlength="4" @keypress.native="allowAlphaNum" @paste.native="pasteAlphaNum"> </md-input>
              <div class="md-error" v-if="errors[0]">
                {{ $t(errors[0]) }}
              </div>
            </md-field>
          </ValidationProvider>
          <ValidationProvider name="name" rules="required" v-slot="{ passed, failed, errors }">
            <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
              <label>{{ $t("suppliers.name") }}</label>
              <md-input v-model="name" type="text"> </md-input>
              <div class="md-error" v-if="errors[0]">
                {{ $t(errors[0]) }}
              </div>
            </md-field>
          </ValidationProvider>
          <md-field>
            <label>{{ $t("suppliers.nameFull") }}</label>
            <md-input v-model="nameFull" type="text"> </md-input>
          </md-field>
          <md-field>
            <label>{{ $t("suppliers.contact") }}</label>
            <md-input v-model="contact" type="text"> </md-input>
          </md-field>
          <ValidationProvider name="email" rules="email" v-slot="{ passed, failed, errors }">
            <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
              <label>{{ $t("suppliers.email") }}</label>
              <md-input v-model="email" type="text"> </md-input>
              <div class="md-error" v-if="errors[0]">
                {{ $t(errors[0]) }}
              </div>
            </md-field>
          </ValidationProvider>
          <ValidationProvider name="leadTimeMonths" rules="min_value:0|decimal:1" v-slot="{ passed, failed, errors }">
            <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
              <label>{{ $t("suppliers.leadTimeMonths") }}</label>
              <md-input v-model="leadTimeMonths" type="number" step="0.1" min="0"> </md-input>
              <span class="md-suffix">{{ $t("suppliers.months") }}</span>
              <div class="md-error" v-if="errors[0]">
                {{ $t(errors[0]) }}
              </div>
            </md-field>
          </ValidationProvider>
          <div style="display: flex; align-items: flex-end; gap: 8px;">
            <div style="flex: 1;">
              <ValidationProvider name="creditor" rules="integer" v-slot="{ passed, failed, errors }">
                <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                  <label>{{ $t("suppliers.creditor") }}</label>
                  <md-input v-model="creditor" type="text" inputmode="numeric" @keypress.native="allowDigits" @paste.native="pasteDigits"> </md-input>
                  <div class="md-error" v-if="errors[0]">
                    {{ $t(errors[0]) }}
                  </div>
                </md-field>
              </ValidationProvider>
            </div>
            <md-button class="md-icon-button md-success md-raised" :title="$t('suppliers.nextFreeCreditor')" @click="fillNextCreditor" style="margin: 0 0 4px 0; min-width: 36px; width: 36px; height: 36px;">
              <md-icon>plus_one</md-icon>
            </md-button>
          </div>
          <md-field>
            <label>{{ $t("suppliers.bankId") }}</label>
            <md-input v-model="bankId" type="text"> </md-input>
          </md-field>
        </ValidationObserver>
      </template>

      <template slot="footer">
        <md-button class="md-secondary mr-1" @click="hideCreateModal">{{ $t("suppliers.close") }}</md-button>
        <md-button class="md-success" @click="createSupplier">{{ $t("suppliers.save") }}</md-button>
      </template>
    </modal>

    <modal v-if="editModal" @close="hideEditModal" class-name="shipping-plan-modal-container">
      <template slot="header">
        <h4 class="modal-title">{{ $t("suppliers.editSupplier") }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="hideEditModal">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <ValidationObserver v-slot="{}" ref="editForm">
          <ValidationProvider name="abbreviation" rules="alpha_num|length:4" v-slot="{ passed, failed, errors }">
            <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
              <label>{{ $t("suppliers.abbreviation") }}</label>
              <md-input v-model="abbreviation" type="text" maxlength="4" @keypress.native="allowAlphaNum" @paste.native="pasteAlphaNum"> </md-input>
              <div class="md-error" v-if="errors[0]">
                {{ $t(errors[0]) }}
              </div>
            </md-field>
          </ValidationProvider>
          <ValidationProvider name="name" rules="required" v-slot="{ passed, failed, errors }">
            <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
              <label>{{ $t("suppliers.name") }}</label>
              <md-input v-model="name" type="text"> </md-input>
              <div class="md-error" v-if="errors[0]">
                {{ $t(errors[0]) }}
              </div>
            </md-field>
          </ValidationProvider>
          <md-field>
            <label>{{ $t("suppliers.nameFull") }}</label>
            <md-input v-model="nameFull" type="text"> </md-input>
          </md-field>
          <md-field>
            <label>{{ $t("suppliers.contact") }}</label>
            <md-input v-model="contact" type="text"> </md-input>
          </md-field>
          <ValidationProvider name="email" rules="email" v-slot="{ passed, failed, errors }">
            <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
              <label>{{ $t("suppliers.email") }}</label>
              <md-input v-model="email" type="text"> </md-input>
              <div class="md-error" v-if="errors[0]">
                {{ $t(errors[0]) }}
              </div>
            </md-field>
          </ValidationProvider>
          <ValidationProvider name="leadTimeMonths" rules="min_value:0|decimal:1" v-slot="{ passed, failed, errors }">
            <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
              <label>{{ $t("suppliers.leadTimeMonths") }}</label>
              <md-input v-model="leadTimeMonths" type="number" step="0.1" min="0"> </md-input>
              <span class="md-suffix">{{ $t("suppliers.months") }}</span>
              <div class="md-error" v-if="errors[0]">
                {{ $t(errors[0]) }}
              </div>
            </md-field>
          </ValidationProvider>
          <ValidationProvider name="creditor" rules="integer" v-slot="{ passed, failed, errors }">
            <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
              <label>{{ $t("suppliers.creditor") }}</label>
              <md-input v-model="creditor" type="text" inputmode="numeric" @keypress.native="allowDigits" @paste.native="pasteDigits"> </md-input>
              <div class="md-error" v-if="errors[0]">
                {{ $t(errors[0]) }}
              </div>
            </md-field>
          </ValidationProvider>
          <md-field>
            <label>{{ $t("suppliers.bankId") }}</label>
            <md-input v-model="bankId" type="text"> </md-input>
          </md-field>
        </ValidationObserver>
      </template>

      <template slot="footer">
        <md-button class="md-secondary mr-1" @click="hideEditModal">{{ $t("suppliers.close") }}</md-button>
        <md-button class="md-success" @click="editSupplier">{{ $t("suppliers.save") }}</md-button>
      </template>
    </modal>
  </div>
</template>
<script>
import { Modal } from "@/components";
import { EventBus } from "@/helpers/event-bus";
import { hasPermission } from "../../mixins/auth";

export default {
  components: {
    Modal,
  },
  data() {
    return {
      createModal: false,
      editModal: false,
      searchQuery: "",
      dockSearchOpen: false,
      abbreviation: null,
      name: null,
      nameFull: null,
      contact: null,
      email: null,
      leadTimeMonths: null,
      creditor: null,
      bankId: null,
      id: null,
    };
  },
  computed: {
    allSuppliers() {
      return this.$store.state.shippingData.supplierNames || [];
    },
    tableData() {
      const q = (this.searchQuery || "").trim().toLowerCase();
      if (!q) return this.allSuppliers;
      return this.allSuppliers.filter((s) =>
        [s.abbreviation, s.name, s.nameFull, s.contact, s.email, s.leadTimeMonths, s.creditor, s.bankId].some(
          (v) => v != null && String(v).toLowerCase().includes(q)
        )
      );
    },
  },
  watch: {
    abbreviation(val) {
      if (val == null) return;
      const clean = String(val).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
      if (clean !== val) this.abbreviation = clean;
    },
    creditor(val) {
      if (val == null) return;
      const clean = String(val).replace(/[^0-9]/g, "");
      if (clean !== String(val)) this.creditor = clean;
    },
  },
  methods: {
    hasPermission(permissionName, tableName, columnName) {
      return hasPermission(permissionName, tableName, columnName);
    },
    toggleDockSearch() {
      this.dockSearchOpen = !this.dockSearchOpen;
      if (this.dockSearchOpen) {
        this.$nextTick(() => {
          const input = this.$refs.dockSearchInput && this.$refs.dockSearchInput.$el;
          if (input) input.focus();
        });
      }
    },
    showCreateModal() {
      this.abbreviation = null;
      this.name = null;
      this.nameFull = null;
      this.contact = null;
      this.email = null;
      this.leadTimeMonths = null;
      this.creditor = null;
      this.bankId = null;
      this.createModal = true;
    },
    hideCreateModal() {
      this.createModal = false;
    },
    hideEditModal() {
      this.editModal = false;
    },
    showEditModal(sn) {
      this.abbreviation = sn.abbreviation;
      this.name = sn.name;
      this.nameFull = sn.nameFull;
      this.contact = sn.contact;
      this.email = sn.email;
      this.leadTimeMonths = sn.leadTimeMonths;
      this.creditor = sn.creditor;
      this.bankId = sn.bankId;
      this.id = sn.id;
      this.editModal = true;
    },
    // Unerlaubte Zeichen gar nicht erst annehmen - beim Tippen ueber keypress, beim Einfuegen
    // aus der Zwischenablage ueber paste (dort loest kein keypress aus).
    allowAlphaNum(e) {
      if (e.key && e.key.length === 1 && !/[a-zA-Z0-9]/.test(e.key)) e.preventDefault();
    },
    allowDigits(e) {
      if (e.key && e.key.length === 1 && !/[0-9]/.test(e.key)) e.preventDefault();
    },
    pasteAlphaNum(e) {
      this.pasteFiltered(e, (text) => text.replace(/[^a-zA-Z0-9]/g, ""));
    },
    pasteDigits(e) {
      this.pasteFiltered(e, (text) => text.replace(/[^0-9]/g, ""));
    },
    pasteFiltered(e, filter) {
      e.preventDefault();
      const el = e.target;
      const clipboard = e.clipboardData || window.clipboardData;
      const clean = filter(clipboard ? clipboard.getData("text") : "");
      const start = el.selectionStart;
      const end = el.selectionEnd;
      let next = el.value.slice(0, start) + clean + el.value.slice(end);
      if (el.maxLength > 0) next = next.slice(0, el.maxLength);
      el.value = next;
      const caret = Math.min(start + clean.length, next.length);
      el.setSelectionRange(caret, caret);
      el.dispatchEvent(new Event("input", { bubbles: true }));
    },
    supplierPayload() {
      return {
        abbreviation: this.abbreviation || null,
        bankId: this.bankId || null,
        name: this.name,
        nameFull: this.nameFull || null,
        contact: this.contact,
        email: this.email,
        leadTimeMonths: this.leadTimeMonths === "" || this.leadTimeMonths === null || this.leadTimeMonths === undefined ? null : parseFloat(this.leadTimeMonths),
        creditor: this.creditor === "" || this.creditor === null || this.creditor === undefined ? null : parseInt(this.creditor, 10),
      };
    },
    notify(type, messageKey) {
      this.$notify({
        timeout: 2500,
        message: this.$t(messageKey),
        icon: "add_alert",
        horizontalAlign: "right",
        verticalAlign: "top",
        type,
      });
    },
    creditorDuplicate() {
      if (this.creditor === "" || this.creditor === null || this.creditor === undefined) return false;
      const c = parseInt(this.creditor, 10);
      return this.allSuppliers.some((s) => s.creditor === c && s.id !== this.id);
    },
    fillNextCreditor() {
      const used = this.allSuppliers.map((s) => s.creditor).filter((c) => c != null);
      this.creditor = used.length ? Math.max(...used) + 1 : 1;
    },
    createSupplier() {
      this.$refs.createForm.validate().then((success) => {
        if (!success) {
          return;
        }
        if (this.creditorDuplicate()) {
          this.notify("danger", "suppliers.messages.creditorExists");
          return;
        }
        this.$store
          .dispatch("shippingData/createSupplier", this.supplierPayload())
          .then(() => {
            this.$notify({
              timeout: 2500,
              message: this.$t("suppliers.messages.created"),
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "success",
            });

            return this.$store.dispatch("shippingData/getSupplierNames");
          })
          .then(() => {
            this.hideCreateModal();
          })
          .catch(() => {
            this.notify("danger", "suppliers.messages.saveFailed");
          });
      });
    },
    editSupplier() {
      this.$refs.editForm.validate().then((success) => {
        if (!success) {
          return;
        }
        if (this.creditorDuplicate()) {
          this.notify("danger", "suppliers.messages.creditorExists");
          return;
        }

        this.$store
          .dispatch("shippingData/editSupplier", {
            id: this.id,
            ...this.supplierPayload(),
          })
          .then(() => {
            this.$notify({
              timeout: 2500,
              message: this.$t("suppliers.messages.updated"),
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "success",
            });

            return this.$store.dispatch("shippingData/getSupplierNames");
          })
          .then(() => {
            this.hideEditModal();
          })
          .catch(() => {
            this.notify("danger", "suppliers.messages.saveFailed");
          });
      });
    },
  },
  created() {
    return this.$store.dispatch("shippingData/getSupplierNames");
  },
  updated() {
    EventBus.$emit("scroll:changed");
  },
};
</script>
<style scoped>
.table-transparent {
  background-color: transparent !important;
}

.mt-0 {
  margin-top: 0 !important;
}

.mr-1 {
  margin-right: 15px;
}

.md-card .md-card-header.md-card-header-icon .material-symbols-outlined {
  width: 33px;
  height: 33px;
  text-align: center;
  line-height: 33px;
  color: #fff;
  margin: 0 !important;
  font-size: 28px !important;
}
</style>
<style>
/* Rechte Andock-Leiste: neu anlegen / suchen (wie im Lieferplan) */
.sp-dock {
  position: fixed;
  top: 110px;
  right: 0;
  z-index: 1031;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
.sp-dock-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid #bbb;
  border-right: 0 none;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
}
.sp-dock-trigger .md-icon {
  color: #fff !important;
}
.sp-dock-trigger--active {
  background: #4caf50;
}
.sp-dock-search {
  position: relative;
}
.sp-dock-search-panel {
  position: absolute;
  right: 44px;
  top: 0;
  width: 320px;
  background: #fff;
  padding: 26px 16px 18px;
  border-radius: 6px 0 0 6px;
  box-shadow: -2px 2px 8px rgba(0, 0, 0, 0.25);
  overflow: visible;
}
.sp-dock-search-panel .md-field {
  margin: 0;
}
</style>
